import { useCallback, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import apiClient from "@/utils/apiClient";
import { useAuth } from "@/App";
import { Lock, Phone, Key, CheckCircle, ArrowRight ,Eye,EyeClosed} from "lucide-react";
import { useNavigate } from "react-router-dom";

const CommonPasswordForgotModal = ({ role }) => {
  const [open, setOpen] = useState(false);
  const { user } = useAuth();
  const handleClick = async () => {
    setOpen(true);
  };
  return (
    <>
      <Button className="w-full" onClick={handleClick}>
        Reset password <Key />
      </Button>
      {open && (
        <CommonPasswordForgotModalContent
          isOpen={open}
          onClose={() => setOpen(false)}
          role={role}
        />
      )}
    </>
  );
};

export default CommonPasswordForgotModal;

const CommonPasswordForgotModalContent = ({ isOpen, onClose, role }) => {
  const [step, setStep] = useState(1); // 1: Enter Phone, 2: Enter OTP, 3: New Password
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { user, logout } = useAuth();
  const [isPasswordVisible, setIsPasswordVisible] = useState({one: false, two: false});
  const handleClose = () => {
    onClose();
  };

  // Step 2: Verify OTP and Reset Password
  const handleResetPassword = async (e) => {
    e.preventDefault();

    if (!newPassword || !confirmPassword) {
      toast.error("Please enter and confirm your new password");
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    if (newPassword.length < 6) {
      toast.error("Password must be at least 6 characters long");
      return;
    }

    const strongPasswordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
    // Explanation:
    // (?=.*[a-z])   → at least one lowercase letter
    // (?=.*[A-Z])   → at least one uppercase letter
    // (?=.*\d)      → at least one digit
    // (?=.*[@$!%*?&]) → at least one special character (customize as needed)
    // [A-Za-z\d@$!%*?&]{8,} → only allowed characters, minimum 8 length

    if (!strongPasswordRegex.test(newPassword)) {
      toast.error(
        "Password must be at least 8 characters long and include: " +
          "uppercase letter, lowercase letter, number, and special character (@$!%*?&)"
      );
      return;
    }

    setLoading(true);
    try {
      await apiClient.post(`/v1/${role}/auth/reset-my-password`, {
        user_id: user?.id || user?.user_id,
        new_password: newPassword,
      });

      toast.success(
        "Password reset successful! You can now login with your new password."
      );
      setStep(2); // Success step

      // Auto close after 10 seconds
      setTimeout(() => {
        handleClose();
      }, 10000);
    } catch (error) {
      toast.error(
        error.response?.data?.detail ||
          "Failed to reset password. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };
  const navigate = useNavigate();

  const goToLogin = useCallback(() => {
    navigate("/");
    logout();
  }, [navigate]);


  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Lock className="w-5 h-5 text-blue-600" />
            Reset Password
          </DialogTitle>
          <DialogDescription>
            {step === 2 && "Password reset successful!"}
          </DialogDescription>
        </DialogHeader>

        {/* Step 1: Enter OTP and New Password */}
        {step === 1 && (
          <form onSubmit={handleResetPassword} className="space-y-4">
            <div>
              <Label htmlFor="newPassword">New Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <Input
                  id="newPassword"
                  type={isPasswordVisible.one ? "text" : "password"}
                  placeholder="Enter new password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="pl-10"
                  disabled={loading}
                />
                {isPasswordVisible.one ? (
                  <EyeClosed className="absolute right-3 top-3 h-4 w-4 text-gray-400 cursor-pointer" onClick={() => {
                    setIsPasswordVisible(prev => ({...prev, one: !prev.one}));
                  }}/>
                ) : (
                  <Eye className="absolute right-3 top-3 h-4 w-4 text-gray-400 cursor-pointer" onClick={() => {
                    setIsPasswordVisible(prev => ({...prev, one: !prev.one}));
                  }}/>
                )}
              </div>
            </div>

            <div>
              <Label htmlFor="confirmPassword">Confirm Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <Input
                  id="confirmPassword"
                  type={isPasswordVisible.two ? "text" : "password"}
                  placeholder="Confirm new password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="pl-10"
                  disabled={loading}
                />
                {isPasswordVisible.two ? (
                  <EyeClosed className="absolute right-3 top-3 h-4 w-4 text-gray-400 cursor-pointer" onClick={() => {
                    setIsPasswordVisible(prev => ({...prev, two: !prev.two}));
                  }}/>
                ) : (
                  <Eye className="absolute right-3 top-3 h-4 w-4 text-gray-400 cursor-pointer" onClick={() => {
                    setIsPasswordVisible(prev => ({...prev, two: !prev.two}));
                  }}/>
                )}
              </div>
            </div>

            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => handleClose()}
                className="flex-1"
                disabled={loading}
              >
                Close
              </Button>
              <Button
                type="submit"
                className="flex-1 btn-primary text-white"
                disabled={loading}
              >
                {loading ? "Resetting..." : "Reset Password"}
              </Button>
            </div>
          </form>
        )}

        {/* Step 2: Success */}
        {step === 2 && (
          <div className="text-center py-6">
            <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">
              Password Reset Successful!
            </h3>
            <p className="text-gray-600 mb-4">
              You can now login with your new password.
            </p>
            <Button onClick={goToLogin} className="btn-primary text-white">
              Go to Login
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
